/**
 * Electricity production maths shared by Phase 5 payouts (§9.1) and the
 * end-game winner evaluation (§11).
 *
 * §1 is strict: "Standard plants require exactly the printed number of matching
 * resource tokens to operate. A plant cannot be operated with fewer or more fuel
 * tokens than its requirement." and "A plant can supply at most its printed
 * number of cities per round." So a plant set is operable only when every plant
 * in it can be saturated from the player's pooled, freely rearrangeable storage.
 */

import type { GameState, PlayerId, PowerDecision, ResourceBundle } from '../types.js';
import { RESOURCE_TYPES, emptyResources } from '../types.js';
import { getPlant } from '../data/plants.js';
import { getPlayer } from './state.js';
import { bundleTotal, maxFlowAssign, rowToBundle, slotsSaturated, type PlantSlot } from './allocation.js';
import { storedPool } from './resources.js';

/** Fuel slots for a chosen set of plants: each must be filled *exactly*. §1. */
export function fuelSlots(plantIds: readonly number[]): PlantSlot[] {
  return plantIds.map((id) => {
    const p = getPlant(id);
    return { accepts: p.accepts.slice(), cap: p.fuel };
  });
}

export function canOperate(
  state: GameState,
  playerId: PlayerId,
  plantIds: readonly number[],
): boolean {
  return slotsSaturated(storedPool(state, playerId), fuelSlots(plantIds));
}

/** Resolve explicit mixes first, then automatically fuel the remaining plants. */
export function powerConsumption(
  state: GameState,
  playerId: PlayerId,
  plantIds: readonly number[],
  fuelByPlant: PowerDecision['fuelByPlant'] = {},
): ResourceBundle | null {
  const remaining = storedPool(state, playerId);
  const consumed = emptyResources();
  for (const [key, fuel] of Object.entries(fuelByPlant)) {
    const id = Number(key);
    if (!plantIds.includes(id) || String(id) !== key) return null;
    const plant = getPlant(id);
    if (RESOURCE_TYPES.some(t => !Number.isInteger(fuel[t]) || fuel[t] < 0 ||
      (fuel[t] > 0 && !plant.accepts.includes(t)))) return null;
    if (bundleTotal(fuel) !== plant.fuel) return null;
    for (const t of RESOURCE_TYPES) {
      remaining[t] -= fuel[t];
      consumed[t] += fuel[t];
      if (remaining[t] < 0) return null;
    }
  }
  const slots = fuelSlots(plantIds.filter(id => fuelByPlant[id] === undefined).sort((a, b) => a - b));
  const { flow, alloc } = maxFlowAssign(remaining, slots);
  if (flow !== slots.reduce((sum, slot) => sum + slot.cap, 0)) return null;
  for (const row of alloc) {
    const fuel = rowToBundle(row);
    for (const t of RESOURCE_TYPES) consumed[t] += fuel[t];
  }
  return consumed;
}

/** All feasible hybrid mixes, including fuel stored on other (even idle) plants. */
export function hybridFuelOptions(
  state: GameState,
  playerId: PlayerId,
  plantIds: readonly number[],
): Record<number, ResourceBundle>[] {
  const hybrids = plantIds.map(getPlant).filter(p => p.accepts.length > 1).sort((a, b) => a.id - b.id);
  const options: Record<number, ResourceBundle>[] = [];
  const visit = (index: number, choice: Record<number, ResourceBundle>): void => {
    const plant = hybrids[index];
    if (!plant) {
      if (powerConsumption(state, playerId, plantIds, choice)) options.push(choice);
      return;
    }
    // Classic hybrid plants accept coal and oil.
    for (let coal = plant.fuel; coal >= 0; coal--) {
      visit(index + 1, { ...choice, [plant.id]: { ...emptyResources(), coal, oil: plant.fuel - coal } });
    }
  };
  visit(0, {});
  return options;
}

export interface SupplyOption {
  plantIds: number[];
  /** Electricity produced — may exceed the player's network; surplus is wasted. §1. */
  capacity: number;
}

/** Every operable plant combination, strongest first. */
export function operableCombinations(state: GameState, playerId: PlayerId): SupplyOption[] {
  const owned = getPlayer(state, playerId).plants.map((p) => p.plantId).sort((a, b) => a - b);
  const pool = storedPool(state, playerId);
  const out: SupplyOption[] = [];
  for (let mask = 0; mask < 1 << owned.length; mask++) {
    const ids = owned.filter((_, i) => (mask & (1 << i)) !== 0);
    if (!slotsSaturated(pool, fuelSlots(ids))) continue;
    out.push({ plantIds: ids, capacity: ids.reduce((s, id) => s + getPlant(id).cities, 0) });
  }
  out.sort((a, b) => b.capacity - a.capacity || a.plantIds.length - b.plantIds.length);
  return out;
}

/** The most cities a player could supply right now — production capped by network size. */
export function bestSupply(
  state: GameState,
  playerId: PlayerId,
): SupplyOption & { cities: number } {
  const network = getPlayer(state, playerId).cities.length;
  let best: SupplyOption & { cities: number } = { plantIds: [], capacity: 0, cities: 0 };
  for (const opt of operableCombinations(state, playerId)) {
    const cities = Math.min(opt.capacity, network);
    if (
      cities > best.cities ||
      (cities === best.cities && opt.plantIds.length < best.plantIds.length)
    ) {
      best = { ...opt, cities };
    }
  }
  return best;
}
